/** One room's guests. childAges holds select values until search. */
export const emptyRoom = (adult = 1) => ({
  adult,
  childAges: [],
})

export const defaultRoomGuests = () => [emptyRoom(2)]

export const resizeRoomGuests = (rooms, nextCount) => {
  const count = Math.min(8, Math.max(1, nextCount))
  const next = (rooms || []).slice(0, count).map((room) => ({
    adult: Math.max(1, Number(room?.adult) || 1),
    childAges: Array.isArray(room?.childAges) ? [...room.childAges] : [],
  }))
  while (next.length < count) next.push(emptyRoom(1))
  return next
}

/** Search / content / booking occupancy shape */
export const occupancyFromRooms = (rooms = []) => (
  rooms.map((room, index) => {
    const ages = (room.childAges || room.child_age || [])
      .map((age) => Number(age))
      .filter((age) => !Number.isNaN(age))
    return {
      room_no: index + 1,
      adult: Math.max(1, Number(room.adult) || 1),
      child: Array.isArray(room.childAges) ? room.childAges.length : ages.length,
      child_age: ages,
    }
  })
)

const legacySplitOccupancy = (rooms, adults, children, childAges) => {
  const roomCount = Math.max(1, Number(rooms) || 1)
  const adultCount = Math.max(Number(adults) || roomCount, roomCount)
  const childCount = Math.max(0, Number(children) || 0)
  const ages = (childAges || []).map((age) => Number(age)).filter((age) => !Number.isNaN(age))

  const occupancy = Array.from({ length: roomCount }, (_, index) => ({
    room_no: index + 1,
    adult: 1,
    child: 0,
    child_age: [],
  }))

  let remainingAdults = adultCount - roomCount
  let adultRoomIndex = 0
  while (remainingAdults > 0) {
    occupancy[adultRoomIndex % roomCount].adult += 1
    remainingAdults -= 1
    adultRoomIndex += 1
  }

  ages.slice(0, childCount).forEach((age, index) => {
    const room = occupancy[index % roomCount]
    room.child += 1
    room.child_age.push(age)
  })

  return occupancy
}

/** Prefer the per-room occupancy saved from the guest picker. */
export const resolveOccupancy = (searchParams = {}) => {
  if (Array.isArray(searchParams.occupancy) && searchParams.occupancy.length) {
    return searchParams.occupancy.map((room, index) => {
      const ages = (room.child_age || room.childAges || [])
        .map((age) => Number(age))
        .filter((age) => !Number.isNaN(age))
      const child = Math.max(0, Number(room.child) || ages.length)
      return {
        room_no: Number(room.room_no) || index + 1,
        adult: Math.max(1, Number(room.adult) || 1),
        child,
        child_age: ages.slice(0, child),
      }
    })
  }
  return legacySplitOccupancy(
    searchParams.rooms,
    searchParams.adults,
    searchParams.children,
    searchParams.childAges
  )
}

export const guestPlanFromOccupancy = (occupancy = []) => (
  occupancy.map((room) => ({
    adultCount: room.adult,
    childCount: room.child,
    childAges: room.child_age || [],
  }))
)

export const occupancyTotals = (occupancy = []) => {
  const adults = occupancy.reduce((sum, room) => sum + (Number(room.adult) || 0), 0)
  const children = occupancy.reduce((sum, room) => sum + (Number(room.child) || 0), 0)
  return {
    rooms: occupancy.length,
    adults,
    children,
    childAges: occupancy.flatMap((room) => room.child_age || []),
  }
}
